/**
 * What the range graph draws for a stretch of the score, built from the
 * grades of one pass: each note's written span, target and grade, the
 * stretch of singing the grader gave it (where it divided the singing into
 * notes), its syllable of the lyrics, and the sung pitch throughout.
 */

// How far past the range the sung pitch is shown
const EDGE_SECONDS = 0.15;
// A pitch reading's length
const HOP_SECONDS = 0.01;

/**
 * The note's syllable in the first verse: { text, wordStart, continues }
 * (continues: more of the word follows on a later note), or null.
 */
export function lyricOf(note) {
  const entries = note?.ParentVoiceEntry?.LyricsEntries;
  if (!entries || typeof entries.keys !== 'function') return null;
  const keys = entries.keys();
  if (!keys.length) return null;
  const verse = [...keys].sort((a, b) => Number(a) - Number(b))[0];
  const entry = entries.getValue(verse);
  if (!entry || !entry.Text) return null;
  const syllables = entry.Word?.Syllables || [];
  const index = syllables.indexOf(entry);
  return {
    text: entry.Text,
    wordStart: index <= 0,
    continues: index >= 0 && index < syllables.length - 1
  };
}

/**
 * The graph for score seconds from..to: { pass, from, to, notes, trace }
 * from the latest pass that graded notes there, or null. notes are the
 * results in score order, each with `span` ({ from, to } of the singing
 * the grader gave it, or null) and `lyric`; trace is [{ time, midi, role }]
 * (role 2: graded, 1: given to a note but not graded, 0: neither).
 */
export function rangeView(results, from, to) {
  const inRange = results.filter(r => r.detail && r.detail.start >= from - 1e-6 && r.detail.start < to - 1e-6);
  if (inRange.length === 0) return null;
  const pass = Math.max(...inRange.map(r => r.pass));
  const notes = inRange.filter(r => r.pass === pass).sort((a, b) => a.detail.start - b.detail.start);

  // Each note's inspection covers it and a little either side: merge them,
  // keeping for each reading the note it was given to
  const points = new Map();
  notes.forEach((r, index) => {
    r.detail.trace.forEach(([time, midi, role]) => {
      if (time < from - EDGE_SECONDS || time > to + EDGE_SECONDS) return;
      const known = points.get(time);
      if (!known) points.set(time, { time, midi, role, owner: role ? index : -1 });
      else if (role > known.role) {
        known.role = role;
        known.owner = index;
      }
    });
  });
  const trace = [...points.values()].sort((a, b) => a.time - b.time);

  return {
    pass,
    from,
    to,
    trace: trace.map(({ time, midi, role }) => ({ time, midi, role })),
    notes: notes.map((r, index) => {
      const own = trace.filter(p => p.owner === index);
      return {
        ...r,
        span: own.length ? { from: own[0].time, to: own[own.length - 1].time + HOP_SECONDS } : null,
        lyric: lyricOf(r.ref)
      };
    })
  };
}
